<template>
  <div>
    <p class="crm-kicker">Configuration</p>
    <h1 class="crm-title">Salons</h1>
    <p class="crm-hint">Événements 1 à 3 jours — stats Journée et archives.</p>

    <div class="crm-card">
      <h3 class="crm-section-title">{{ form.id ? 'Modifier' : 'Nouveau salon' }}</h3>
      <div class="crm-field">
        <label class="crm-label">Nom</label>
        <input class="crm-input" v-model="form.name" placeholder="JDC Paris 2027" />
      </div>
      <div class="crm-field">
        <label class="crm-label">Ville</label>
        <input class="crm-input" v-model="form.city" placeholder="Paris" />
      </div>
      <div class="crm-grid-2">
        <div class="crm-field">
          <label class="crm-label">Début</label>
          <input class="crm-input" type="date" v-model="form.start_date" />
        </div>
        <div class="crm-field">
          <label class="crm-label">Fin</label>
          <input class="crm-input" type="date" v-model="form.end_date" />
        </div>
      </div>
      <p class="crm-hint" v-if="durationHint">{{ durationHint }}</p>
      <div class="crm-btn-stack">
        <button type="button" class="crm-btn crm-btn-primary" :disabled="saving || !canSave" @click="save">
          {{ saving ? '…' : (form.id ? 'Enregistrer' : 'Créer') }}
        </button>
        <button v-if="form.id" type="button" class="crm-btn crm-btn-outline" @click="resetForm">Annuler</button>
      </div>
      <p v-if="msg" class="crm-hint" style="margin-top:10px;margin-bottom:0">{{ msg }}</p>
    </div>

    <div class="crm-card tight">
      <div class="crm-salon-list-head">
        <label class="crm-label" style="margin:0">Salons</label>
        <span v-if="refreshing" class="crm-loader-inline">Mise à jour…</span>
      </div>
      <label class="crm-salon-check">
        <input type="checkbox" v-model="showArchived" @change="onToggleArchived" />
        <span class="crm-hint" style="margin:0">Inclure archives</span>
      </label>

      <div v-if="loading && !events.length" class="crm-loader-block">
        <div class="crm-spinner"></div>
        <p class="crm-hint" style="margin:0">Chargement des salons…</p>
      </div>

      <template v-else>
        <div v-for="e in events" :key="e.id" class="crm-salon-event-card">
          <div class="crm-salon-event-head">
            <div class="crm-salon-event-info">
              <div class="crm-row-title">{{ e.name }}</div>
              <div class="crm-row-meta">
                {{ e.city || '—' }} · {{ formatDateRange(e.start_date, e.end_date) }}
                · {{ e.days ? e.days.length : '?' }} j
              </div>
            </div>
            <span class="crm-badge" :class="badgeClass(e)">{{ statusLabel(e) }}</span>
          </div>
          <div class="crm-salon-event-actions">
            <button type="button" class="crm-btn crm-btn-outline crm-btn-compact" @click="edit(e)">Modifier</button>
            <button
              v-if="!e.archived"
              type="button"
              class="crm-btn crm-btn-soft crm-btn-compact"
              @click="archive(e)"
            >Archiver</button>
          </div>
        </div>
        <p v-if="!loading && !events.length" class="crm-hint" style="margin:8px 0">
          Aucun salon — créez-en un ci-dessus (ou vérifiez le patch SQL / API).
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import {
  getCachedEvents,
  setCachedEvents,
  invalidateSalonCaches
} from './crmSalonCache'
import { formatDateRange } from './crmSalonUtils'

export default {
  name: 'CrmSalonSalons',
  data() {
    const showArchived = true
    const cached = getCachedEvents(showArchived) || []
    return {
      events: cached,
      showArchived,
      loading: !cached.length,
      refreshing: false,
      saving: false,
      msg: '',
      form: {
        id: null,
        name: '',
        city: '',
        start_date: '',
        end_date: ''
      }
    }
  },
  computed: {
    daySpan() {
      if (!this.form.start_date || !this.form.end_date) return 0
      const a = new Date(this.form.start_date + 'T00:00:00')
      const b = new Date(this.form.end_date + 'T00:00:00')
      if (isNaN(a) || isNaN(b) || b < a) return 0
      return Math.round((b - a) / 86400000) + 1
    },
    canSave() {
      return !!(this.form.name && this.form.start_date && this.form.end_date && this.daySpan >= 1 && this.daySpan <= 3)
    },
    durationHint() {
      if (!this.form.start_date || !this.form.end_date) return 'Durée autorisée : 1 à 3 jours.'
      if (this.daySpan < 1) return 'Date de fin invalide.'
      if (this.daySpan > 3) return 'Trop long : maximum 3 jours.'
      return this.daySpan + ' jour(s) — OK'
    }
  },
  mounted() {
    this.load({ soft: !!this.events.length })
  },
  methods: {
    formatDateRange,
    statusLabel(e) {
      if (e.is_current) return 'En cours'
      if (e.is_future) return 'Futur'
      return 'Archivé'
    },
    badgeClass(e) {
      if (e.is_current) return 'ok'
      if (e.is_future) return ''
      return 'navy'
    },
    resetForm() {
      this.form = { id: null, name: '', city: '', start_date: '', end_date: '' }
      this.msg = ''
    },
    edit(e) {
      this.form = {
        id: e.id,
        name: e.name || '',
        city: e.city || '',
        start_date: e.start_date || '',
        end_date: e.end_date || ''
      }
      this.msg = ''
    },
    onToggleArchived() {
      const cached = getCachedEvents(this.showArchived)
      if (cached && cached.length) {
        this.events = cached
        this.load({ soft: true })
      } else {
        this.events = []
        this.load({ soft: false })
      }
    },
    async load(opts) {
      const soft = !!(opts && opts.soft)
      if (soft) this.refreshing = true
      else this.loading = true
      this.msg = ''
      try {
        const { data } = await this.$apollo.query({
          client: 'AUTH',
          query: require('src/graphql/auth/crmSalon/crmSalonEvents.gql'),
          variables: { include_archived: !!this.showArchived },
          fetchPolicy: 'network-only'
        })
        this.events = data.crmSalonEvents || []
        setCachedEvents(this.showArchived, this.events)
        // Aussi garder une copie « avec archives » pour Journée
        if (this.showArchived) {
          setCachedEvents(true, this.events)
        }
      } catch (e) {
        if (!this.events.length) {
          this.msg = (e && e.message) || 'Chargement impossible'
        } else {
          this.msg = 'Hors ligne — affichage du cache local'
        }
      } finally {
        this.loading = false
        this.refreshing = false
      }
    },
    async save() {
      if (!this.canSave) return
      this.saving = true
      this.msg = ''
      try {
        const { data } = await this.$apollo.mutate({
          client: 'AUTH',
          mutation: require('src/graphql/auth/crmSalon/crmSalonUpsertEvent.gql'),
          variables: {
            id: this.form.id || null,
            name: this.form.name.trim(),
            city: this.form.city || null,
            start_date: this.form.start_date,
            end_date: this.form.end_date
          }
        })
        const saved = data && data.crmSalonUpsertEvent
        invalidateSalonCaches()
        if (saved && saved.id) {
          const idx = this.events.findIndex(x => String(x.id) === String(saved.id))
          if (idx >= 0) this.$set(this.events, idx, Object.assign({}, this.events[idx], saved))
          else this.events = [saved].concat(this.events)
        }
        this.msg = 'Salon enregistré'
        this.resetForm()
        await this.load({ soft: true })
      } catch (e) {
        this.msg = (e && e.message) || 'Enregistrement impossible'
      } finally {
        this.saving = false
      }
    },
    async archive(e) {
      if (!confirm('Archiver « ' + e.name + ' » ?')) return
      try {
        await this.$apollo.mutate({
          client: 'AUTH',
          mutation: require('src/graphql/auth/crmSalon/crmSalonArchiveEvent.gql'),
          variables: { id: e.id }
        })
        invalidateSalonCaches()
        await this.load({ soft: true })
      } catch (err) {
        this.msg = (err && err.message) || 'Archive impossible'
      }
    }
  }
}
</script>
